import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'
import Auth from './components/Auth'
import Dashboard from './components/Dashboard'
import AdminPanel from './components/admin/AdminPanel'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import { ThemeProvider } from './context/ThemeContext'
import { CurrencyProvider } from './context/CurrencyContext'
import './App.css'

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAdmin, setShowAdmin] = useState(false)
  const [appSettings, setAppSettings] = useState({
    app_name: 'DebtCrush',
    primary_color: '#c9a84c',
    app_logo_url: ''
  })

  useEffect(() => {
    let initialResolved = false

    // This only guards the very first "stop showing Loading..." moment — it
    // does NOT gate session updates themselves, so sign-in/sign-out/token
    // refresh keep working normally for the lifetime of the app.
    function stopInitialLoading() {
      if (initialResolved) return
      initialResolved = true
      setLoading(false)
    }

    // Safety net: the initial session check can occasionally hang and never
    // come back (seen after certain Google sign-in redirect chains). If
    // nothing has resolved within 8 seconds, stop waiting and fall back to
    // the sign-in screen instead of hanging on "Loading..." forever.
    const timeoutId = setTimeout(stopInitialLoading, 8000)

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session)
        stopInitialLoading()
      })
      .catch(() => stopInitialLoading())

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (_event === 'SIGNED_IN' && session?.user?.id) {
          const now = new Date().toISOString()
          await supabase.from('profiles')
            .update({ last_login_at: now, last_seen_at: now })
            .eq('id', session.user.id)
        }
        setSession(session)
        stopInitialLoading()
      }
    )
    fetchAppSettings()
    if (window.location.pathname === '/admin') setShowAdmin(true)
    return () => { clearTimeout(timeoutId); subscription.unsubscribe() }
  }, [])

  async function fetchAppSettings() {
    const { data } = await supabase
      .from('app_settings')
      .select('*')
      .eq('id', 1)
      .single()
    if (data) setAppSettings(data)
  }

  if (window.location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />
  }

  if (window.location.pathname === '/terms-of-service') {
    return <TermsOfService />
  }

  if (loading) {
    return (
      <ThemeProvider><CurrencyProvider>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100vh', background: 'var(--bg)',
          fontFamily: 'sans-serif', flexDirection: 'column', gap: '1rem'
        }}>
          <div style={{ fontSize: '2rem' }}>💸</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading {appSettings.app_name}...</div>
        </div>
      </CurrencyProvider></ThemeProvider>
    )
  }

  if (!session) return <ThemeProvider><CurrencyProvider><Auth appSettings={appSettings} /></CurrencyProvider></ThemeProvider>

  if (showAdmin) {
    return (
      <ThemeProvider><CurrencyProvider>
        <AdminPanel
          session={session}
          appSettings={appSettings}
          onExit={() => { setShowAdmin(false); window.history.pushState({}, '', '/') }}
          onSettingsSaved={fetchAppSettings}
        />
      </CurrencyProvider></ThemeProvider>
    )
  }

  return (
    <ThemeProvider><CurrencyProvider>
      <Dashboard
        session={session}
        appSettings={appSettings}
        onOpenAdmin={() => { setShowAdmin(true); window.history.pushState({}, '', '/admin') }}
      />
    </CurrencyProvider></ThemeProvider>
  )
}

export default App